import { DocumentDialogComponent } from '@sdbn/tmc-legacy';

import { MatDialog } from '@angular/material/dialog';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { TaskPosition } from './../../model/task-position';
import { TaskType } from './../../model/task-type.enum';
import { TransportTask } from './../../model/transport-task';
import { TaskAssignmentService } from './../../service/task-assignment.service';
import { CompleteTransportAssignment, TransportCompletionOption } from '../../model/transport-completion-option';
import { DialogService, ResponseMessage } from '@sdbn/common';
import { TaskProcessingHintDialogComponent } from './task-processing-hint.dialog.component';
import { TaskCompletion } from '../../model/task-completion';
import { DocumentTaskUpdate } from '../../model/document-task-update';
import { ContextChange } from '@sdbn/common';
import { AbstractWidgetComponent } from '@sdbn/common';
import { AppClientConfigurationService } from '@sdbn/common';
import { TranslationService } from '@sdbn/common';

@Component({
    selector: 'app-task-position-details',
    templateUrl: './task-position-details.component.html',
    styleUrls: ['./task-position-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TaskPositionDetailsComponent extends AbstractWidgetComponent {

  responseMessages: ResponseMessage[] = [];

  requestRunning = false;

  _transportId: number;

  selectedTask: TransportTask;

  @Output() detailsChanged = new EventEmitter<void>();

  @Input() set transportId(id: number) {
    this._transportId = id;
    this.loadTasks();
  }

  taskPositions: TaskPosition[] = [];

  constructor(
    private cps: AppClientConfigurationService,
    private taskAssignmentService: TaskAssignmentService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    public translationService: TranslationService) {
    super();
  }

  private loadTasks() {

    this.responseMessages = [];
    this.requestRunning = true;

    this.taskAssignmentService.loadPositionTasks({ id: this._transportId }).subscribe(result => {
      this.taskPositions = result;
      this.restoreSelection();
      this.requestRunning = false;
    }, error => {
      this.requestRunning = false;
      console.error('Error loading TaskPositions', error);
    });
  }

  private restoreSelection() {
    if (this.selectedTask != null) {
      for (const tp of this.taskPositions) {
        for (const t of tp.tasks) {
          if (this.selectedTask != null && t.id === this.selectedTask.id) {
            this.selectedTask = t;
          }
        }
      }
    }
  }

  onContextChange(contextChange: ContextChange) {

    if (contextChange.key === 'selected-transport') {
      const transport = this.dashboardContext.context.get('selected-transport');
      if (transport != null && transport.id != null && this._transportId != transport.id) {
        this._transportId = transport.id;
        this.loadTasks();
      }
    }
  }

  onSetElementAndContext() {
    const transport = this.dashboardContext.context.get('selected-transport');
    if (transport != null && transport.id != null && this._transportId != transport.id) {
      this._transportId = transport.id;
      this.loadTasks();
    }
  }

  private triggerReloadTransport() {
    if (this.dashboardContext != null) {
      this.dashboardContext.setContextValue('transport-changed', new Date().getTime());
    }
  }

  /**
   * Handles the completion of tasks, except tasks of type DOCUMENT.
   */
  public completeTask(task: TransportTask) {

    this.requestRunning = true;
    this.responseMessages = [];

    const completion = new TaskCompletion();
    completion.taskId = task.id;
    completion.transportId = this._transportId;
    completion.documentIds = task.documentIds;


    this.taskAssignmentService.completeTask(completion).
      subscribe(result => {
        this.requestRunning = false;
        this.handleCompleteTransportAssignment();
        this.loadTasks();
        this.detailsChanged.next();
        this.triggerReloadTransport();
      }, error => {
        this.requestRunning = false;
        console.error('Error loading TaskPositions', error);
      });
  }

  public updateDocumentTask(task: TransportTask) {

    this.requestRunning = true;
    this.responseMessages = [];

    const update = new DocumentTaskUpdate();
    update.taskId = task.id;
    update.transportId = this._transportId;
    update.documentIds = task.documentIds;

    this.taskAssignmentService.updateDocumentTask(update).
      subscribe(result => {
        this.requestRunning = false;
        this.loadTasks();
        this.detailsChanged.next();
        this.triggerReloadTransport();
      }, error => {
        this.requestRunning = false;
        console.error('Error loading TaskPositions', error);
      });
  }

  /**
   * Handles the process of DOCUMENT enabled tasks. A separated editor will be shown to maintain document and to store it.
   * As a result, the task will be completed and connected to created document.
   * @param task the current selected task
   */
  public completeDocumentTask(task: TransportTask) {

    const dialogRef = this.dialog.open(DocumentDialogComponent, {
      height: '90%',
      width: '90%',
      maxHeight: '1600px',
      maxWidth: '1024px',
      data: {
        documentIds: task.documentIds,
        documentTemplateId: task.documentTemplateId,
        addDocumentsEnabled: true,
        metaData: {
          "TransportIdentity": { id: this._transportId }
        }
      },
      panelClass: 'tmc-dialog-container'
    });
    dialogRef.afterClosed().subscribe(storedDocuments => {
      if (storedDocuments != null) {

        task.documentIds = [];
        for (const doc of storedDocuments) {
          task.documentIds.push(doc.id);
        }

        if (!task.done) {
          this.completeTask(task);
        } else {
          this.updateDocumentTask(task);
        }
      }
    });

  }

  public isMessageTask(t: TransportTask): boolean {
    return t != null && t.type === TaskType.MESSAGE;
  }

  public isDocumentTask(t: TransportTask): boolean {
    return t != null && t.type === TaskType.DOCUMENT;
  }

  public getDocumentTaskTitle(t: TransportTask): string {
    // TODO translate: translationService
    return t.documentIds != null && t.documentIds.length > 0 ? 'Dokument bearbeiten' : 'Dokument erfassen';
  }

  public isCheckTask(t: TransportTask): boolean {
    return t == null ? true : t.type === TaskType.CHECK;
  }

  public isPictureTask(t: TransportTask): boolean {
    return t != null && t.type === TaskType.PICTURE;
  }

  public selectTask(t: TransportTask) {
    this.selectedTask = t;
  }

  public isCompleteMessageTaskDisabled(selectedTask: TransportTask) {
    if (this.requestRunning) {
      return true;
    }

    if (selectedTask == null) {
      return true;
    }

    return false;
  }

  public isCompleteTaskDisabled(selectedTask: TransportTask) {
    return this.requestRunning || selectedTask == null || selectedTask.done || (selectedTask.type === TaskType.ATTRIBUTE_CHANGE);
  }

  public completePictureTask(event: Event) {

    this.requestRunning = true;
    this.responseMessages = [];

    const files = (event.target as HTMLInputElement).files;
    if (files != null) {
      for (let i = 0; i < files.length; i++) {
        const current: File = files.item(i);
        const name = current.name;
        const type = current.type;
        const reader = new FileReader();
        reader.onload = () => {
          this.taskAssignmentService.completeDocumentTask(this._transportId, this.selectedTask.id, type, name, reader.result).subscribe(
            next => {
              this.loadTasks();
              this.handleCompleteTransportAssignment();
              this.detailsChanged.next();
              this.triggerReloadTransport();
              this.requestRunning = false;
            },
            error => {
              this.loadTasks();
              this.detailsChanged.next();
              this.triggerReloadTransport();
              console.error('completing task failed', error);
              this.requestRunning = false;
            });
        };
        reader.readAsArrayBuffer(current);
      }
    }
  }

  private handleCompleteTransportAssignment() {

    if (this.cps.isPropertyTrue('feature.complete-transport-assisgnment-after-task', false)) {

      this.requestRunning = true;
      this.responseMessages = [];

      this.taskAssignmentService.getTransportCompletionOption({ id: this._transportId }).subscribe(
        option => {
          if (option != null) {
            this.completeTransportAssignmentWithOption(option);
          }
          this.requestRunning = false;
        }, error => {
          this.requestRunning = false;
        }
      );
    }
  }

  private completeTransportAssignmentWithOption(option: TransportCompletionOption) {

    this.responseMessages = [];
    this.requestRunning = true;


    this.dialogService.showConfirm(
      'tmc-yard.ui.task-assignment-dialog.complete-transport-assignment.confirm-dialog.title',
      'tmc-yard.ui.task-assignment-dialog.complete-transport-assignment.confirm-dialog.message'
    ).afterClosed().subscribe(result => {
      if (result) {
        const completeAssignmentRequest = new CompleteTransportAssignment();
        completeAssignmentRequest.transportId = this._transportId;
        completeAssignmentRequest.sourceStationId = option.sourceStationId;
        completeAssignmentRequest.targetStationId = option.targetStationId;
        completeAssignmentRequest.routingOptionType = option.routingOptionType;
        this.taskAssignmentService.completeTransportAssignment(completeAssignmentRequest).subscribe(
          response => {
            this.requestRunning = false;

            this.responseMessages = response.responseMessages;
            this.detailsChanged.next();
            this.triggerReloadTransport();
          }, error => {
            console.error('transport assignment completion failed', error);
            this.requestRunning = false;
          }
        );
      }
    });

  }

  public showProcessingHint(task: TransportTask) {
    //   this.dialog.open(TaskProcessingHintDialogComponent, {
    //     data: task.processingHint,
    //     panelClass: 'task-processing-panel'
    //   });

    this.dialogService.openMaxHeight(TaskProcessingHintDialogComponent, task.processingHint);
  }

}
